import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import {
  fetchAffiliates,
  fetchAffiliateBalanceTransactions,
  recordAffiliatePayout,
} from '@/lib/affiliates'
import { ApiClientError } from '@/lib/api/client'
import type { Affiliate, AffiliateBalanceTransaction } from '@/types/affiliates'
import styles from './AfiliadosSaldoPage.module.css'

function formatCurrency(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function AfiliadosSaldoPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const affiliateIdParam = searchParams.get('affiliate_id')

  const [affiliates, setAffiliates] = useState<Affiliate[]>([])
  const [selectedId, setSelectedId] = useState<number | ''>('')
  const [transactions, setTransactions] = useState<AffiliateBalanceTransaction[]>([])
  const [pagination, setPagination] = useState<{
    current_page: number
    per_page: number
    total: number
    last_page: number
  } | null>(null)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [payoutAmount, setPayoutAmount] = useState('')
  const [payoutNote, setPayoutNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const manualAffiliates = useMemo(
    () => affiliates.filter((a) => a.payout_method === 'manual'),
    [affiliates]
  )

  const loadAffiliates = useCallback(async () => {
    const res = await fetchAffiliates()
    setAffiliates(res.affiliates)
  }, [])

  const loadLedger = useCallback(async (affiliateId: number, p: number) => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchAffiliateBalanceTransactions(affiliateId, {
        page: p,
        per_page: 20,
      })
      setTransactions(res.items)
      setPagination(res.pagination)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar extrato.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadAffiliates()
  }, [loadAffiliates])

  useEffect(() => {
    if (affiliateIdParam && manualAffiliates.length > 0) {
      const id = Number.parseInt(affiliateIdParam, 10)
      if (Number.isFinite(id) && manualAffiliates.some((a) => a.id === id)) {
        setSelectedId(id)
      }
    }
  }, [affiliateIdParam, manualAffiliates])

  useEffect(() => {
    if (selectedId === '') {
      setTransactions([])
      setPagination(null)
      return
    }
    void loadLedger(selectedId, page)
  }, [selectedId, page, loadLedger])

  const handleSelectAffiliate = (id: number | '') => {
    setSelectedId(id)
    setPage(1)
    setFormError(null)
    setPayoutAmount('')
    setPayoutNote('')
    if (id === '') {
      setSearchParams({})
    } else {
      setSearchParams({ affiliate_id: String(id) })
    }
  }

  const handlePayout = async (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedId === '') return
    setFormError(null)
    const n = parseFloat(payoutAmount.replace(',', '.'))
    if (Number.isNaN(n) || n < 0.01) {
      setFormError('Informe um valor válido (mín. R$ 0,01).')
      return
    }
    setIsSubmitting(true)
    try {
      await recordAffiliatePayout(selectedId, {
        amount: n,
        note: payoutNote.trim() || null,
      })
      setPayoutAmount('')
      setPayoutNote('')
      await loadAffiliates()
      await loadLedger(selectedId, 1)
      setPage(1)
    } catch (err) {
      setFormError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível registrar o pagamento.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedAffiliate = useMemo(
    () =>
      selectedId === '' ? null : affiliates.find((a) => a.id === selectedId) ?? null,
    [affiliates, selectedId]
  )
  const currentBalance = selectedAffiliate?.balance ?? null

  /** Com `?affiliate_id=` (ex.: vindo da lista), o afiliado já está definido — sem texto introdutório nem dropdown. */
  const isDeepLinkToAffiliate = affiliateIdParam != null && affiliateIdParam !== ''

  return (
    <div className={styles.page}>
      {!isDeepLinkToAffiliate && (
        <>
          <p className={styles.intro}>
            Afiliados em <strong>pagamento manual</strong> acumulam comissão como saldo interno após
            vendas confirmadas. Registre aqui os pagamentos que você efetuar fora da plataforma para dar
            baixa no saldo.
          </p>

          <div className={styles.toolbar}>
            <label className={styles.selectLabel}>
              <span>Afiliado (manual)</span>
              <select
                className={styles.select}
                value={selectedId === '' ? '' : String(selectedId)}
                onChange={(e) => {
                  const v = e.target.value
                  handleSelectAffiliate(v === '' ? '' : Number.parseInt(v, 10))
                }}
              >
                <option value="">Selecione…</option>
                {manualAffiliates.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.affiliate_name} — saldo {formatCurrency(a.balance)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {manualAffiliates.length === 0 && !isLoading && (
        <div className={styles.empty}>
          <Icon icon="mdi:wallet-outline" className={styles.emptyIcon} />
          <p>Nenhum afiliado com pagamento manual. Crie ou edite um afiliado na lista e escolha “Manual”.</p>
        </div>
      )}

      {selectedId !== '' && (
        <>
          <div className={styles.balanceCard}>
            <span className={styles.balanceLabel}>Saldo atual</span>
            <span className={styles.balanceValue}>
              {currentBalance !== null ? formatCurrency(currentBalance) : '—'}
            </span>
          </div>

          <form className={styles.payoutForm} onSubmit={(e) => void handlePayout(e)}>
            <h3 className={styles.formTitle}>Registrar pagamento (baixa)</h3>
            <div className={styles.formRow}>
              <label className={styles.field}>
                <span>Valor pago (R$) *</span>
                <input
                  className={styles.input}
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                />
              </label>
              <label className={styles.fieldGrow}>
                <span>Observação (opcional)</span>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Ex.: PIX enviado, TED, etc."
                  value={payoutNote}
                  onChange={(e) => setPayoutNote(e.target.value)}
                  maxLength={2000}
                />
              </label>
            </div>
            {formError && (
              <div className={styles.formError}>
                <Icon icon="mdi:alert-circle-outline" />
                {formError}
              </div>
            )}
            <button type="submit" className={styles.btnSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Salvando…' : 'Confirmar baixa'}
            </button>
          </form>

          <h3 className={styles.sectionTitle}>Extrato</h3>
          {isLoading ? (
            <div className={styles.centered}>
              <Icon icon="mdi:loading" className={styles.spinner} />
              Carregando…
            </div>
          ) : error ? (
            <div className={styles.centered}>{error}</div>
          ) : transactions.length === 0 ? (
            <div className={styles.centeredMuted}>Nenhuma movimentação ainda.</div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Valor</th>
                    <th>Detalhe</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <tr key={t.id}>
                      <td>{formatDate(t.created_at)}</td>
                      <td>
                        {t.type === 'commission_credit' ? (
                          <span className={styles.badgeCredit}>Crédito (venda)</span>
                        ) : t.type === 'simulation_credit' ? (
                          <span className={styles.badgeSim}>Crédito (simulação)</span>
                        ) : (
                          <span className={styles.badgeDebit}>Pagamento (baixa)</span>
                        )}
                      </td>
                      <td className={t.type === 'payout_debit' ? styles.debitAmt : styles.creditAmt}>
                        {t.type === 'payout_debit' ? '−' : '+'}
                        {formatCurrency(t.amount)}
                      </td>
                      <td className={styles.noteCell}>
                        {t.type === 'payout_debit' && t.note ? t.note : ''}
                        {t.type === 'commission_credit' && t.payment_id != null && (
                          <span className={styles.muted}>Pagamento #{t.payment_id}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && pagination.last_page > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <span>
                Página {pagination.current_page} de {pagination.last_page}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={page >= pagination.last_page}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
